import React, {useEffect, useState, forwardRef, useImperativeHandle} from 'react';
import {Button} from "primereact/button";
import {InputNumber} from "primereact/inputnumber";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CampaignService} from "../../service/CampaignService";
import {subscription} from "../../utils/Utils";
import {scheduleRowToLocal, scheduleRowToUtc} from "../../utils/TimeUtil";
import DaySelector from "./DaySelector";

const campaignService = new CampaignService();

const clone = (o) => JSON.parse(JSON.stringify(o || null));

// Saatler sunucuda UTC saklanır, kullanıcı yerel saatinde seçer. UTC<->yerel
// dönüşümü (gece yarısını geçince gün kaydırması + 7="Never" sentinel'ini koruma)
// ortak yardımcılarda: utils/TimeUtil (scheduleRowToLocal / scheduleRowToUtc).

// Keep the local schedule rows in sync with the desired count: trim extras or
// add rows +5h from the last, inheriting the price-update flags.
const syncRows = (rows, count) => {
    const next = rows.slice(0, count);
    let hh = next.length ? next[next.length - 1].hh : 9;
    const mm = next.length ? next[next.length - 1].mm : 0;
    while (next.length < count) {
        hh = (hh + 5) % 24;
        next.push({hh, mm, job_days: [], auto_price_update: true, notify: true, auto_suggested_price_update: true, headless_browser_enabled: false});
    }
    return next;
};

// wizardMode: kampanya ekleme sihirbazında kendi "Kaydet" butonu gizlenir;
// kaydetme, sihirbazın "İleri" butonundan ref ile tetiklenir (save() Promise döndürür).
const PriceCheckOnboardingStep = forwardRef(({t, refresh, wizardMode}, ref) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // The number of daily checks is set by the admin elsewhere; here we just
    // edit the time/days of the existing rows. Load them in local time.
    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        const settings = selectedCampaign?.settings || {};
        const apply = (storedRows) => {
            const localRows = (storedRows || []).map(scheduleRowToLocal);
            const desired = settings.daily_price_check_count
                || localRows.length || subscription()?.daily_price_check_count || 1;
            setRows(syncRows(localRows, desired));
            setLoading(false);
        };
        if (settings.scraping_jobs && settings.scraping_jobs.length) {
            apply(clone(settings.scraping_jobs));
        } else {
            campaignService.scrapingJobHours(campaignId)
                .then(r => apply(r || []))
                .catch(() => apply([]));
        }
    }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateRow = (i, key, value) => setRows(prev => {
        const next = prev.slice();
        next[i] = {...next[i], [key]: value};
        return next;
    });

    // Resolves to true on success, false on failure (so the wizard's "İleri"
    // can decide whether to advance). Never rejects.
    const save = () => {
        setSaving(true);
        const baseSettings = clone(selectedCampaign?.settings) || {};
        const merged = {...baseSettings, scraping_jobs: rows.map(scheduleRowToUtc)};
        return campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged).then(() => {
            selectedCampaign.settings = merged;
            if (loadCampaigns) loadCampaigns();
            toast.current.show({severity: 'success', summary: t('onboarding.priceCheck.saved', 'Fiyat kontrolü kaydedildi'), detail: t('message_detail.successful')});
            if (refresh) refresh();
            return true;
        }).catch(error => {
            setError(error);
            setError(null);
            return false;
        }).finally(() => setSaving(false));
    };

    // Expose save() to the wizard so its "İleri" button can persist this step.
    useImperativeHandle(ref, () => ({save}));

    return (
        <div>
            <style>{`.cmpz-time .p-inputnumber-button{background:#eef0f3;border:1px solid #e5e7eb;color:#4b5563}
                .cmpz-time .p-inputnumber-button:enabled:hover{background:#e2e6eb;color:#111827}`}</style>
            <h2 style={{margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 10}}>
                <i className="pi pi-clock"/> {t('onboarding.step.price_check.label', 'Fiyat Kontrolü')}
            </h2>
            <div style={{maxWidth: 680, marginBottom: 18}}>
                <Message severity="info" style={{width: '100%'}}
                         text={t('onboarding.priceCheck.note', 'Bu saatler, kampanyadaki ürünler için fiyat kontrolü (price check) işinin başlama saatidir. Saatler yerel saatinize göre gösterilir.')}/>
            </div>

            {loading
                ? <p style={{color: '#9ca3af'}}>{t('onboarding.loading', 'Yükleniyor…')}</p>
                : <>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 680}}>
                        {rows.map((r, i) => {
                            return (
                                <div key={i} style={{border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, background: '#fff'}}>
                                    <div style={{fontWeight: 700, marginBottom: 14, color: '#111827'}}>
                                        {t('onboarding.priceCheck.checkN', '{n}. kontrol').replace('{n}', i + 1)}
                                    </div>

                                    {/* Time */}
                                    <div style={{display: 'flex', alignItems: 'flex-end', gap: 18, marginBottom: 18}}>
                                        <div>
                                            <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 4}}>{t('onboarding.priceCheck.hour', 'Saat')}</label>
                                            <InputNumber className="cmpz-time" value={r.hh} min={0} max={23} showButtons buttonLayout="horizontal" inputStyle={{width: 56, textAlign: 'center'}}
                                                         incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                         onValueChange={(e) => updateRow(i, 'hh', e.value ?? 0)}/>
                                        </div>
                                        <span style={{fontWeight: 700, fontSize: '1.2rem', color: '#9ca3af', paddingBottom: 6}}>:</span>
                                        <div>
                                            <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 4}}>{t('onboarding.priceCheck.minute', 'Dakika')}</label>
                                            <InputNumber className="cmpz-time" value={r.mm} min={0} max={59} step={5} showButtons buttonLayout="horizontal" inputStyle={{width: 56, textAlign: 'center'}}
                                                         incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                         onValueChange={(e) => updateRow(i, 'mm', e.value ?? 0)}/>
                                        </div>
                                    </div>

                                    {/* Days */}
                                    <div>
                                        <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 6}}>{t('onboarding.priceCheck.days', 'Günler')}</label>
                                        <DaySelector value={r.job_days} onChange={(v) => updateRow(i, 'job_days', v)} t={t}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Kendi Kaydet butonu — kampanya ekleme sihirbazında gizli (İleri kaydeder). */}
                    {!wizardMode &&
                    <div style={{marginTop: 18, maxWidth: 680, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button label={t('actions.save', 'Kaydet')} icon="pi pi-save" className="p-button-success"
                                loading={saving} disabled={saving} onClick={save}/>
                    </div>}
                </>}
        </div>
    );
});

export default PriceCheckOnboardingStep;
